import React from 'react';
import ReactModal from 'react-modal';

import { Flex } from '@rover/kibble/core';
import {
  usePreventScroll,
  useReactModalSetup,
} from '@rover/kibble/official-patterns/Modal/Modal.common';

import useOverlayFocus, { FOCUSABLE_SELECTOR } from './hooks/useOverlayFocus';
import { overlayCommonProps, OverlayProps } from './Overlay.common';

const APP_ELEMENT_SELECTOR = '#base-content';

// react-modal only wraps Shift+Tab from its own content node, so wrap it from this surface too.
const wrapShiftTab = (event: React.KeyboardEvent<HTMLElement>): void => {
  if (event.key !== 'Tab' || !event.shiftKey || event.target !== event.currentTarget) return;
  const controls = event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  const lastControl = controls[controls.length - 1];
  if (!lastControl) return;
  event.preventDefault();
  event.stopPropagation();
  lastControl.focus();
};

const Overlay = ({
  children,
  collapseMobilePx,
  onClose,
  ariaLabel,
  centerVertically,
}: OverlayProps): JSX.Element => {
  const { modalId } = useReactModalSetup(APP_ELEMENT_SELECTOR);
  usePreventScroll(true, modalId);

  const { contentRef } = useOverlayFocus();

  return (
    <ReactModal
      isOpen
      shouldReturnFocusAfterClose={false}
      shouldFocusAfterRender={false}
      contentRef={contentRef}
      onRequestClose={onClose}
      contentLabel={ariaLabel}
      style={{
        overlay: { display: 'contents' },
        content: { display: 'contents' },
      }}
    >
      <Flex
        {...overlayCommonProps}
        alignItems={centerVertically ? 'stretch' : 'start'}
        position="fixed"
        width="100vw"
        // A click on anything unfocusable inside lands focus here rather than on the body
        webProps={{ tabIndex: -1, onKeyDown: wrapShiftTab }}
        testID="image-gallery-overlay"
        sx={{
          ...overlayCommonProps.sx,
          outline: 'none',
          overflowY: 'scroll',
          px: collapseMobilePx ? ['0x', '4x'] : overlayCommonProps.sx.px,
        }}
      >
        {children}
      </Flex>
    </ReactModal>
  );
};

export default Overlay;
